import { useEffect, useRef, useState } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Formik, FormikProps } from 'formik'

import {
  MainContainer,
  ImpactForm,
  FormTimestamp,
  HelperWrapper,
  LocaleHeaderTitle,
} from 'src/components'
import { useCheckImpactCodeExist, useEditImpact, useImpact } from 'src/hooks'

import { impactSchema } from 'src/common/validations'
import { ETranslationNs, IImpactForm } from 'src/types'
import { ROUTES } from 'src/constants'
import { generateFormState } from './form/state.form'
import { joinAbsRoute } from 'src/common/utils'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/langauges-list/constants'
import { useAccountSubscription } from 'src/hooks/subscriptions/use-account-subscription.hook'

const EditImpactScreen = () => {
  const [t] = useTranslation(ETranslationNs.Navbar)
  const { mutateAsync: editImpact, isLoading, isSuccess } = useEditImpact()
  const [locale, setLocale] = useState(DEFAULT_LANGUAGE_CODE)
  let { accountId } = useParams()
  const theme = useTheme()
  const { data: impact, isLoading: isImpactLoading } = useImpact(locale)
  const [isMeasurable, setIsMeausarble] = useState(
    !!(impact?.quantity_type || impact?.quantity || impact?.period || impact?.period_start),
  )

  const [enableReinitialize, setEnableReinitialize] = useState(false)
  const initialIsMeasurable = useRef(isMeasurable)

  const { mutateAsync: checkCodeTaken } = useCheckImpactCodeExist()
  const formikRef = useRef<FormikProps<IImpactForm>>(null)
  const { data: accountSubscription } = useAccountSubscription(accountId ?? '')
  const location = useLocation()
  const stateParams = location?.state

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  useEffect(() => {
    if (impact && !isImpactLoading) {
      setEnableReinitialize(false)
    }
  }, [impact, isImpactLoading])

  useEffect(() => {
    if (impact) {
      setIsMeausarble(
        !!(impact.quantity_type || impact.quantity || impact.period || impact.period_start),
      )
      initialIsMeasurable.current = !!(
        impact.quantity_type ||
        impact.quantity ||
        impact.period ||
        impact.period_start
      )
    }
  }, [impact])

  if (!impact) {
    return null
  }
  const handleLocaleChange = (locale: string) => {
    setEnableReinitialize(true)

    setLocale(() => locale)
  }

  const handleSubmit = async (values: IImpactForm) => {
    if (formikRef.current) {
      const isCodeTaken = await checkCodeTaken(values.code)
      if (isCodeTaken && isCodeTaken.message) {
        formikRef.current.setErrors({ code: isCodeTaken.message })
        return
      }
    }

    if (!isMeasurable) {
      await editImpact({
        ...values,
        quantity: '',
        quantity_type: null,
        period: null,
        period_start: null,
        oldIcon: impact?.image,
        oldMedia: impact?.media,
        locale,
      })
      return
    }

    await editImpact({
      ...values,
      oldIcon: impact?.image,
      oldMedia: impact?.media,
      locale,
    })
  }

  const handleIsMeasurable = (val: boolean) => {
    setIsMeausarble(val)
  }

  if (isSuccess) {
    return (
      <Navigate to={joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.IMPACTS)} state={stateParams} />
    )
  }

  const initialValues = generateFormState(impact)

  return (
    <MainContainer
      headerCode={'#' + impact.code}
      doNotShowCodeOnMobile
      headerTitle={
        isTablet ? (
          <LocaleHeaderTitle
            headerCode={'#' + impact.code}
            // locale={locale}
            // handleLocale={handleLocaleChange}
            title={impact.name || t('impacts-header-title.v')}
          />
        ) : (
          <HelperWrapper t="impacts-header-title" ns={ETranslationNs.Navbar} />
        )
      }
      navigateBack
      withAccountIcon
      withScanBtn
    >
      <Formik
        enableReinitialize={enableReinitialize}
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={impactSchema(isMeasurable)}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        <ImpactForm
          disabled={!accountSubscription}
          locale={locale}
          handleLocale={handleLocaleChange}
          isLoading={isLoading}
          isMeasurable={isMeasurable}
          handleMeasurableUpdate={handleIsMeasurable}
          initialIsMeasurable={initialIsMeasurable.current}
        />
      </Formik>
      <FormTimestamp
        created_at={impact.created?.created_at}
        created_by={impact.created?.user_name ?? impact.created?.user_email}
        updated_at={impact.updated?.created_at}
        updated_by={impact.updated?.user_name ?? impact.updated?.user_email}
      />
    </MainContainer>
  )
}

export default EditImpactScreen
